/**
 * WorkingRestaurant.js
 * ─────────────────────────────────────────────────────────────────────────────
 * "Which restaurant's menu am I editing?" answered once, for every menu
 * product form.
 *
 * The create and edit dish forms each asked the operator to pick the
 * restaurant from a dropdown, even when they had just opened the form from
 * that restaurant's own menu. Worse, both loaded option groups, options and
 * the phone-shoot picker for the SIGNED-IN USER's id: for a vendor that is the
 * restaurant owner and happens to work, for Lusuku staff it is the staff
 * account, so an admin editing Taste Budz was offered nobody's option groups
 * and created new options owned by themselves.
 *
 * A dish's restaurant is a fact about the dish (or about the menu the form was
 * opened from), not a choice on the form. Moving a dish to another restaurant
 * is not something this form offers.
 *
 * Two ids, because the data model uses both:
 *   ref      what the dish's `restaurantId` holds (restaurant _id OR owner id);
 *            saved back unchanged.
 *   ownerId  the restaurant owner's user id; options and option groups are
 *            keyed by it (the menu importer uses the same key).
 */
import React, { useEffect, useState } from "react";
import { FaStore } from "react-icons/fa";
import { apiFetchRestaurants } from "../../api/index";

/**
 * @param {string|null} ref     restaurant _id or owner id
 * @param {object|null} hint    what the caller already knows ({ name, ownerId, imageUrl })
 *                              so the field renders at once when opened from the menu drawer
 */
export const useWorkingRestaurant = (ref, hint = null) => {
  const [restaurant, setRestaurant] = useState(() =>
    ref && hint?.name ? { ref: String(ref), ...hint } : null
  );
  const [loading, setLoading] = useState(Boolean(ref));

  useEffect(() => {
    if (!ref) {
      setLoading(false);
      return undefined;
    }
    let alive = true;
    setLoading(true);
    // Accepts either id; returns this one restaurant for its owner or staff.
    apiFetchRestaurants(ref, false)
      .then(({ data }) => {
        const list = Array.isArray(data?.restaurants) ? data.restaurants : [];
        const wanted = String(ref);
        const match =
          list.find((r) => String(r._id) === wanted || String(r.owner) === wanted) || null;
        if (!alive || !match) return;
        setRestaurant({
          ref: wanted,
          ownerId: String(match.owner || wanted),
          name: match.name,
          imageUrl: match.imageUrl || null,
          commissionRate: match.commissionRate,
          commissionConfigured: Boolean(match.commissionConfigured),
        });
      })
      .catch(() => {})
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [ref]);

  return { restaurant, loading };
};

/** The restaurant a dish belongs to, shown, not chosen. */
export const WorkingRestaurantField = ({ restaurant, loading, labelClassName, missingText }) => (
  <div className="mb-3">
    <div className={labelClassName || "form-label"} style={{ marginBottom: 6 }}>
      Restaurant
    </div>
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "10px 14px",
        borderRadius: 10,
        border: "1px solid #e2e8f0",
        background: "#f8fafc",
      }}
      aria-live="polite"
    >
      {restaurant?.imageUrl ? (
        <img
          src={restaurant.imageUrl}
          alt=""
          style={{ width: 36, height: 36, borderRadius: 8, objectFit: "cover", background: "#fff" }}
        />
      ) : (
        <span
          style={{
            width: 36, height: 36, borderRadius: 8, background: "#e0f2fe", color: "#0369a1",
            display: "inline-flex", alignItems: "center", justifyContent: "center",
          }}
        >
          <FaStore />
        </span>
      )}
      <div style={{ minWidth: 0 }}>
        <div style={{ fontWeight: 700, color: "#0f172a", fontSize: "1rem" }}>
          {restaurant?.name || (loading ? "Loading…" : missingText || "No restaurant on this dish")}
        </div>
        <div style={{ fontSize: "0.78rem", color: "#64748b" }}>
          This dish belongs to this restaurant&apos;s menu.
        </div>
      </div>
    </div>
  </div>
);

export default WorkingRestaurantField;
